import { useEffect } from "react";
import { LDContext, useLDClient } from "launchdarkly-react-client-sdk";
import { useSetRecoilState } from "recoil";
import {
  ldIdentifyPendingState,
  ldSyncedContextKeyState,
} from "./recoil/atoms";

const getContextKey = (context: LDContext | undefined): string | undefined =>
  context && "key" in context && typeof context.key === "string"
    ? context.key
    : undefined;

/**
 * LDProvider only evaluates its initial context; it does not re-identify when
 * the context prop changes. Without this, the client stays on the anonymous
 * context after login, so lims-targeted flags (e.g. the Spanish toggle) never
 * turn on for authenticated patients. Push the context into the client whenever
 * it changes.
 *
 * identify() is asynchronous, and flag values read before it settles still
 * belong to the previous context. The synced-key atom records which context
 * key the client has finished switching to, so identity-sensitive flag
 * readers (e.g. the standalone fetal-sex rollout) can ignore values that were
 * evaluated for someone else.
 */
export const LdContextSync = ({ context }: { context: LDContext }): null => {
  const ldClient = useLDClient();
  const setLdSyncedContextKey = useSetRecoilState(ldSyncedContextKeyState);
  const setLdIdentifyPending = useSetRecoilState(ldIdentifyPendingState);
  useEffect(() => {
    if (!ldClient) {
      return undefined;
    }
    const contextKey = getContextKey(context);
    // The client only counts as "switching" when it is currently evaluating a
    // different key. Booting straight into this context (a full page load for
    // an already-authenticated patient) is not a switch, so readers may trust
    // getContext() immediately instead of waiting a redundant round trip.
    const isSwitchingIdentity =
      contextKey !== undefined &&
      getContextKey(ldClient.getContext()) !== contextKey;
    setLdIdentifyPending(isSwitchingIdentity);

    let cancelled = false;
    ldClient
      .identify(context)
      .then(() => {
        if (!cancelled && contextKey !== undefined) {
          setLdSyncedContextKey(contextKey);
        }
      })
      .catch(() => {
        // Keep the previous synced key; identity-sensitive readers fall back
        // to their own fail-closed behavior (e.g. the rollout legacy timeout).
      })
      .finally(() => {
        if (!cancelled) {
          setLdIdentifyPending(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ldClient, context, setLdSyncedContextKey, setLdIdentifyPending]);
  return null;
};
